import React, { FC, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'app/store';
import { setDrawerOpen } from 'features/ui/model/uiSlice';
import CloseIcon from '@mui/icons-material/Close';
import {
  Drawer,
  Typography,
  IconButton,
  TextField,
  MenuItem,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
} from '@mui/material';
import { DrawerContent } from 'shared/ui/Drawer/DrawerStyles';
import { Controller, useForm } from 'react-hook-form';
import dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { createNewCompetitionThunk, updateCompetitionThunk } from 'entities/competitions-b2b/thunk';
import { yupResolver } from '@hookform/resolvers/yup';
import { ICompetitionsCreateFeatureForm } from '../../../../features/competitions-b2b/types';
import { ImageUpload } from '../../../../shared/ui/ImageUpload/ui/ImageUpload';
import { PrizeListFeature } from '../../../../features/prize/list';
import { useGetMechanicsQuery } from '../../../../features/getMechanics/api/getMechanicsApi';
import { useGetAdminCompetitionItemQuery } from '../../../../entities/competitions-b2b/api';
import { validationSchema } from '../../../../features/competitions-b2b/lib';
import { ErrorText } from '../../../../shared/ui/error-text';
import css from '../ParticipantsXlsxLoader/style.module.css';

type IDrawerCompetition = {
  competitionId?: number;
};

const DrawerCompetition: FC<IDrawerCompetition> = ({ competitionId }) => {
  const dispatch = useAppDispatch();
  const { isDrawerOpen, actionDrawer } = useAppSelector((state) => state.ui);
  const { data, refetch } = useGetAdminCompetitionItemQuery({ competition_id: competitionId });
  const { data: skuList } = useGetMechanicsQuery({});
  const [loading, setLoading] = useState(false);
  const [isNeedCheckImage, setIsNeedCheckImage] = useState(false);

  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    setError,
    reset,
    watch,
    formState: { errors: formErrors },
  } = useForm<ICompetitionsCreateFeatureForm>({
    defaultValues: {
      description: '',
      description_short: '',
      name: '',
      date_start: '',
      date_end: '',
      date_award_start: '',
      date_award_end: '',
    },
    resolver: yupResolver(validationSchema) as any,
    mode: 'all',
  });

  const handleOnSubmit = handleSubmit(async () => {
    const errors = Object.keys(formErrors).length > 0;
    if (errors) {
      return;
    }
    setLoading(true);
    const data = getValues();

    if (!competitionId) {
      await dispatch(createNewCompetitionThunk(data));
      reset();
      // navigate(`/promo/competitions-b2b/${competition.id}`);
    } else await dispatch(updateCompetitionThunk({ ...data, id: competitionId })).then(() => reset());

    setLoading(false);
  });

  const uploadImage = async (file: File) => {
    const formData = new FormData();
    formData.append('image_main', file);

    setValue('image_main', file);
    setIsNeedCheckImage(true);
  };

  /**
   * Получить время с разницой в n-дней
   */
  const getDateDiff = (value: string, days: number) => {
    const date = new Date(value);
    date.setDate(date.getDate() + days);

    return dayjs(date);
  };

  useEffect(() => {
    if (data) {
      reset({
        description: data.description,
        description_short: data.description_short,
        name: data.name,
        date_start: data.date_start,
        date_end: data.date_end,
        date_award_start: data.date_award_start,
        date_award_end: data.date_award_end,
        objective_id: data.objective_id,
      });
    }
  }, [data, reset]);

  return (
    <Drawer
      anchor="right"
      open={isDrawerOpen}
      onClose={() => dispatch(setDrawerOpen(false))}
    >
      <DrawerContent style={{ width: '800px' }}>
        <div className="header">
          <Typography
            variant="h5"
            sx={{ fontWeight: '600' }}
          >
            {actionDrawer === 'create' ? 'Создание соревнования' : 'Изменение соревнования'}
          </Typography>
          <IconButton
            onClick={() => dispatch(setDrawerOpen(false))}
            sx={{ color: '#000' }}
          >
            <CloseIcon />
          </IconButton>
        </div>
        <div className="content">
          <div>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <Box
                  sx={{
                    marginBottom: '24px',
                  }}
                >
                  <TextField
                    type="text"
                    label="Название"
                    placeholder=""
                    fullWidth
                    size="small"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    error={formErrors.name && true}
                    {...field}
                  />
                  {formErrors.name ? <ErrorText>{formErrors.name.message}</ErrorText> : null}
                </Box>
              )}
            />

            <Controller
              name="description_short"
              control={control}
              render={({ field }) => (
                <Box
                  sx={{
                    marginBottom: '24px',
                  }}
                >
                  <TextField
                    type="text"
                    label="Краткое описание"
                    placeholder=""
                    fullWidth
                    size="small"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    error={formErrors.description_short && true}
                    {...field}
                  />
                  {formErrors.description_short ? <ErrorText>{formErrors.description_short.message}</ErrorText> : null}
                </Box>
              )}
            />
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <Box
                  sx={{
                    marginBottom: '12px',
                  }}
                >
                  <TextField
                    multiline
                    type="text"
                    label="Описание"
                    placeholder=""
                    fullWidth
                    size="small"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    error={formErrors.description && true}
                    {...field}
                  />
                  {formErrors.description ? <ErrorText>{formErrors.description.message}</ErrorText> : null}
                </Box>
              )}
            />

            <Typography
              mt={1}
              mb={1}
            >
              Период проведения
            </Typography>
            <Box sx={{ display: 'flex', marginBottom: '32px' }}>
              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale="en-gb"
              >
                <Controller
                  name="date_start"
                  control={control}
                  render={({ field: { value, onChange, ref }, fieldState: { error } }) => (
                    <DatePicker
                      label="Дата начала"
                      value={value ? dayjs(value) : null}
                      disablePast
                      onChange={(newValue) => onChange(newValue ? dayjs(newValue).format('YYYY-MM-DD') : null)}
                      slots={{ textField: TextField }}
                      slotProps={{
                        textField: {
                          error: !!error,
                          helperText: error ? error.message : '',
                          inputRef: ref,
                          sx: { marginRight: 2 },
                        },
                      }}
                    />
                  )}
                />
              </LocalizationProvider>
              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale="en-gb"
              >
                <Controller
                  name="date_end"
                  control={control}
                  render={({ field: { value, onChange, ref }, fieldState: { error } }) => (
                    <DatePicker
                      label="Дата окончания"
                      value={value ? dayjs(value) : null}
                      disablePast
                      onChange={(newValue) => onChange(newValue ? dayjs(newValue).format('YYYY-MM-DD') : null)}
                      slots={{ textField: TextField }}
                      slotProps={{
                        textField: {
                          error: !!error,
                          helperText: error ? error.message : '',
                          inputRef: ref,
                          sx: { marginRight: 2 },
                        },
                      }}
                    />
                  )}
                />
              </LocalizationProvider>
            </Box>

            <Typography
              mt={1}
              mb={1}
            >
              Период вывода бонусов
            </Typography>
            <Box sx={{ display: 'flex', marginBottom: '32px' }}>
              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale="en-gb"
              >
                <Controller
                  control={control}
                  name="date_award_start"
                  defaultValue=""
                  render={({ field: { value, onChange, ...restField } }) => (
                    <div>
                      <DatePicker
                        label="Дата начала"
                        value={value ? dayjs(value) : null}
                        sx={{ marginRight: 2 }}
                        minDate={getDateDiff(getValues('date_end'), 1)}
                        onError={(err) => {
                          if (err) {
                            setError('date_award_start', {
                              type: 'custom',
                              message: 'Неверная дата',
                            });
                          }
                        }}
                        onChange={(value) => onChange(value ? dayjs(value).format('YYYY-MM-DD') : null)}
                        {...restField}
                      />
                      {formErrors.date_award_start ? (
                        <ErrorText>{formErrors.date_award_start.message}</ErrorText>
                      ) : null}
                    </div>
                  )}
                />
              </LocalizationProvider>
              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale="en-gb"
              >
                <Controller
                  control={control}
                  name="date_award_end"
                  defaultValue=""
                  render={({ field: { value, onChange, ...restField } }) => (
                    <div>
                      <DatePicker
                        label="Дата окончания"
                        value={value ? dayjs(value) : null}
                        minDate={getDateDiff(getValues('date_award_start'), 1)}
                        onError={(err) => {
                          if (err) {
                            setError('date_award_end', {
                              type: 'custom',
                              message: 'Неверная дата',
                            });
                          }
                        }}
                        onChange={(value) => onChange(value ? dayjs(value).format('YYYY-MM-DD') : null)}
                        {...restField}
                      />
                      {formErrors.date_award_end ? <ErrorText>{formErrors.date_award_end.message}</ErrorText> : null}
                    </div>
                  )}
                />
              </LocalizationProvider>
            </Box>

            <Controller
              control={control}
              name="objective_id"
              render={({ field }) => (
                <Box
                  sx={{
                    marginBottom: '24px',
                  }}
                >
                  <FormControl fullWidth>
                    <InputLabel id="select-label-type">Механика</InputLabel>
                    <Select
                      disabled={!skuList}
                      labelId="select-label-type"
                      label="Механика"
                      error={!!formErrors.objective_id}
                      {...field}
                    >
                      {skuList?.items.map((item) => (
                        <MenuItem value={item.id}>{item.name}</MenuItem>
                      ))}
                    </Select>
                    {!skuList ? (
                      <div
                        className={css.loader}
                        style={{ top: 28 }}
                      >
                        <CircularProgress size={20} />
                      </div>
                    ) : null}
                  </FormControl>
                  {formErrors.objective_id ? <ErrorText>{formErrors.objective_id.message}</ErrorText> : null}
                </Box>
              )}
            />

            <Controller
              control={control}
              name="image_main"
              render={({ field }) => (
                <div>
                  <ImageUpload
                    title="Фото"
                    src={field.value}
                    callback={(val) => uploadImage(val)}
                    isLoading={false}
                    isSuccess={false}
                    silent
                  />
                  {!watch().image_main && isNeedCheckImage ? (
                    <ErrorText>Изображение является обязательным</ErrorText>
                  ) : null}
                </div>
              )}
            />
          </div>
          <Box
            sx={{
              display: 'flex',
              marginTop: '30px',
            }}
          >
            <Button
              variant="contained"
              onClick={() => {
                setIsNeedCheckImage(true);
                handleOnSubmit();
              }}
              sx={{ textTransform: 'none' }}
            >
              {data?.id ? 'Сохранить' : 'Создать'}
            </Button>
            {loading ? 'Загрузка' : null}
          </Box>
          <Box sx={{ marginTop: '30px' }}>
            {data?.id ? (
              <PrizeListFeature
                competitionId={data.id}
                refetchCompetition={refetch}
              />
            ) : null}
          </Box>
        </div>
      </DrawerContent>
    </Drawer>
  );
};

export default DrawerCompetition;
